// Чистата логика на справката „Инвентаризации": филтрите в колоните (Склад ·
// Въвел · Причина · Излишък · Липси · Статус), търсенето, сортирането, групите
// по склад и KPI. Броячите в попъпите са фасетни — по периода след търсенето
// и ОСТАНАЛИТЕ филтри (`components/filters/columnFilterModel.ts`).
// „Излишък" и „Липси" са два отделни въпроса с/без — „покажи
// инвентаризациите с липси" е един клик. Сортирането и групирането са тук (не
// в таблицата), за да не разкъсат груповите ленти.
import type { StocktakeDocument } from "../../../../api/inventoryStocktakes";
import {
  isStocktakeAnnulled,
  stocktakeReasonOf,
  stocktakeUserOf,
  stocktakeWarehouseOf,
} from "../../stocktake/components/stocktakeMeta";
import type { FilterSpecs } from "../../components/filters/columnFilterModel";

export type StocktakeStatusKey = "active" | "annulled";

export type StocktakeFilters = {
  warehouses: string[];
  users: string[];
  reasons: string[];
  surplus: string[]; // yes | no
  shortage: string[]; // yes | no
  statuses: string[]; // StocktakeStatusKey
};

export const EMPTY_STOCKTAKE_FILTERS: StocktakeFilters = {
  warehouses: [],
  users: [],
  reasons: [],
  surplus: [],
  shortage: [],
  statuses: [],
};

export type StocktakeFilterKey = keyof StocktakeFilters;

export type StocktakeSortKey =
  | "date"
  | "warehouse"
  | "user"
  | "reason"
  | "surplus"
  | "shortage"
  | "net"
  | "gross";
export type StocktakeSort = { key: StocktakeSortKey; dir: "asc" | "desc" };

// Текстовите колони тръгват от А, числовите и датата — от най-голямото
export const TEXT_SORT_KEYS: StocktakeSortKey[] = ["warehouse", "user", "reason"];

export const statusKeyOf = (stocktake: StocktakeDocument): StocktakeStatusKey =>
  isStocktakeAnnulled(stocktake) ? "annulled" : "active";

// Има ли излишък / липса в документа — стойностите на филтрите с/без
export const hasSurplusOf = (stocktake: StocktakeDocument) => (stocktake.surplusCount ? "yes" : "no");
export const hasShortageOf = (stocktake: StocktakeDocument) => (stocktake.shortageCount ? "yes" : "no");

export type StocktakeFilterLabels = {
  titles: Record<StocktakeFilterKey, string>;
  surplusLabel: (value: string) => string;
  shortageLabel: (value: string) => string;
  statusLabel: (value: string) => string;
};

// „—" (липсващ склад/потребител/причина) не е опция — както досега
export const stocktakeFilterSpecs = ({
  titles,
  surplusLabel,
  shortageLabel,
  statusLabel,
}: StocktakeFilterLabels): FilterSpecs<StocktakeDocument, StocktakeFilters> => ({
  warehouses: {
    kind: "list",
    title: titles.warehouses,
    valuesOf: (stocktake) => [stocktakeWarehouseOf(stocktake)],
    skipValue: "—",
  },
  users: { kind: "list", title: titles.users, valuesOf: (stocktake) => [stocktakeUserOf(stocktake)], skipValue: "—" },
  reasons: {
    kind: "list",
    title: titles.reasons,
    valuesOf: (stocktake) => [stocktakeReasonOf(stocktake)],
    skipValue: "—",
    searchable: true,
  },
  surplus: {
    kind: "list",
    title: titles.surplus,
    valuesOf: (stocktake) => [hasSurplusOf(stocktake)],
    labelOf: surplusLabel,
    order: ["yes", "no"],
  },
  shortage: {
    kind: "list",
    title: titles.shortage,
    valuesOf: (stocktake) => [hasShortageOf(stocktake)],
    labelOf: shortageLabel,
    order: ["yes", "no"],
  },
  statuses: {
    kind: "list",
    title: titles.statuses,
    valuesOf: (stocktake) => [statusKeyOf(stocktake)],
    labelOf: statusLabel,
    order: ["active", "annulled"],
  },
});

const matchesQuery = (stocktake: StocktakeDocument, words: string[]) => {
  const haystack = [
    `№${stocktake.id}`,
    stocktakeWarehouseOf(stocktake),
    stocktakeUserOf(stocktake),
    stocktake.reason ?? "",
    stocktake.deleteReason ?? "",
    ...stocktake.lines.map((line) => line.productName),
    // приготвените рецепти („шопска" намира инвентаризацията с тях)
    ...(stocktake.recipes ?? []).map((recipe) => recipe.recipeName),
  ]
    .join(" ")
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
};

// Търсачката над таблицата — базата, върху която работят филтрите в колоните
export const searchStocktakes = (stocktakes: StocktakeDocument[], query: string) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.length ? stocktakes.filter((stocktake) => matchesQuery(stocktake, words)) : stocktakes;
};

const COMPARATORS: Record<
  StocktakeSortKey,
  (a: StocktakeDocument, b: StocktakeDocument) => number
> = {
  date: (a, b) => a.date.localeCompare(b.date),
  warehouse: (a, b) => stocktakeWarehouseOf(a).localeCompare(stocktakeWarehouseOf(b), "bg"),
  user: (a, b) => stocktakeUserOf(a).localeCompare(stocktakeUserOf(b), "bg"),
  reason: (a, b) => stocktakeReasonOf(a).localeCompare(stocktakeReasonOf(b), "bg"),
  surplus: (a, b) => (a.surplusCount || 0) - (b.surplusCount || 0),
  shortage: (a, b) => (a.shortageCount || 0) - (b.shortageCount || 0),
  net: (a, b) => (a.totalValueNoVat || 0) - (b.totalValueNoVat || 0),
  gross: (a, b) => (a.totalValueWithVat || 0) - (b.totalValueWithVat || 0),
};

export const sortStocktakes = (stocktakes: StocktakeDocument[], sort: StocktakeSort) => {
  const sign = sort.dir === "asc" ? 1 : -1;
  const compare = COMPARATORS[sort.key];
  return [...stocktakes].sort((a, b) => compare(a, b) * sign || b.id - a.id);
};

// Ред на таблицата: запис или лента на група (склад)
export type StocktakeListRow =
  | { kind: "row"; stocktake: StocktakeDocument }
  | { kind: "group"; name: string; count: number };

// Групите по склад в реда на първата си поява → сортирането на колоната се
// пази и вътре в групата. Броят в лентата е по всички показани (не само
// видимите до „Покажи още").
export const toListRows = (
  visible: StocktakeDocument[],
  all: StocktakeDocument[],
  groupByWarehouse: boolean,
): StocktakeListRow[] => {
  if (!groupByWarehouse)
    return visible.map((stocktake) => ({ kind: "row", stocktake }));
  const countByWarehouse = new Map<string, number>();
  for (const stocktake of all) {
    const name = stocktakeWarehouseOf(stocktake);
    countByWarehouse.set(name, (countByWarehouse.get(name) ?? 0) + 1);
  }
  const byWarehouse = new Map<string, StocktakeDocument[]>();
  for (const stocktake of visible) {
    const name = stocktakeWarehouseOf(stocktake);
    byWarehouse.set(name, [...(byWarehouse.get(name) ?? []), stocktake]);
  }
  const out: StocktakeListRow[] = [];
  for (const [name, group] of byWarehouse) {
    out.push({ kind: "group", name, count: countByWarehouse.get(name) ?? group.length });
    out.push(...group.map((stocktake) => ({ kind: "row" as const, stocktake })));
  }
  return out;
};

// KPI и редът „Общо" — само активните (анулираните не влизат)
export const stocktakeTotals = (stocktakes: StocktakeDocument[]) => {
  const active = stocktakes.filter((stocktake) => !isStocktakeAnnulled(stocktake));
  return {
    count: active.length,
    surplus: active.reduce((sum, s) => sum + (s.surplusCount || 0), 0),
    shortage: active.reduce((sum, s) => sum + (s.shortageCount || 0), 0),
    net: active.reduce((sum, s) => sum + (s.totalValueNoVat || 0), 0),
    gross: active.reduce((sum, s) => sum + (s.totalValueWithVat || 0), 0),
  };
};
